import { action, extendObservable } from 'mobx';
import FetchService from '../../../core/services/FetchService';
import DraftItemListService from './DraftItemListService';
import NotificationService from '../../../core/services/NotificationService';
import ConfirmationDialogService from '../../../core/services/ConfirmationDialogService';
import { VideoUploadService } from '../../../core/services/VideoUploadService';
import set from 'lodash/set';
import get from 'lodash/get';
import { isValidAudioDuration, isValidImageSize } from '../../../core/validations';

class DraftItemAssetsUploadService {
  submit = new FetchService();

  constructor(onItemAssetUploadPercentageChange) {
    extendObservable(this, {
      itemId: null,
      itemType: null,
      field: null,
      replaceFile: false,
    });
    this.onItemAssetUploadPercentageChange = onItemAssetUploadPercentageChange;
  }

  init = action((itemId, itemType, field, replaceFile) => {
    this.itemId = itemId;
    this.itemType = itemType;
    this.field = field;
    this.replaceFile = replaceFile;
  });

  getEndpointUrl = () => {
    return this.field === 'video'
      ? `/draft-items/${this.itemId}/video/request-upload`
      : `/draft-items/${this.itemId}/${this.field}/upload`;
  };

  updateItemInDraftList = action((props) => {
    DraftItemListService.items = DraftItemListService.items.map((item) => {
      if (item.id === this.itemId) {
        return {
          ...set({ ...item }, this.field, get(props, this.field)),
          ...(this.field === 'video' && { videoLink: props.videoLink }),
          audioAssetsStatus: props.audioAssetsStatus,
          imageAssetsStatus: props.imageAssetsStatus,
          videoAssetsStatus: props.videoAssetsStatus,
        };
      }
      return item;
    });
  });

  handleUpload = async (file) => {
    if (
      this.field === 'audio' &&
      ['GAP_FILL', 'PRONUNCIATION', 'SPEECH_PRACTICE', 'PRESENTATION', 'UNSCRAMBLE_SPEECH_RECOGNITION'].includes(
        this.itemType
      )
    ) {
      const valid = await isValidAudioDuration(file);
      if (!valid) {
        NotificationService.addNotification(
          'Item Types Gap Fill, Pronunciation, Speech Practice, Presentation e Unscramble Speech Recognition only accepted mp3 audios less than 30 seconds.',
          'error'
        );
        return;
      }
    }

    if (this.field === 'image' && !isValidImageSize(file)) {
      NotificationService.addNotification('Only images smaller than 1MB can be uploaded.', 'error');
      return;
    }

    const uploadFile = () => {
      const formData = new FormData();
      formData.append(this.field, file);

      this.submit
        .fetch({
          method: 'patch',
          url: this.getEndpointUrl(),
          body: this.field === 'video' ? { videoFileSize: file.size } : formData,
        })
        .then(async () => {
          if (this.submit.data) {
            if (this.field === 'video') {
              const videoUploadService = new VideoUploadService();
              videoUploadService.init();
              videoUploadService
                .handleUpload(file, this.submit.data.videoUploadLink)
                .subscribe((uploadPercentage) => this.onItemAssetUploadPercentageChange(this.field, uploadPercentage));
            }
            this.updateItemInDraftList(this.submit.data);
          }
          if (this.submit.error) {
            NotificationService.addNotification(`Error to upload ${this.field}`, 'error');
          }
        });
    };

    if (this.replaceFile) {
      ConfirmationDialogService.show('Replace file', 'Would you like to change the current file?', () => {
        uploadFile();
      });
    } else {
      uploadFile();
    }
  };
}

export default DraftItemAssetsUploadService;
